import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';

import AppLayout from './components/shared/AppLayout';
import ProtectedRoute from './components/shared/ProtectedRoute';
import { applyRouteSeo } from './lib/seo';

import LoginPage from './pages/auth/LoginPage';
import SignupPage from './pages/auth/SignupPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';
import VerifyEmailPage from './pages/auth/VerifyEmailPage';
import BookDetail from './pages/BookDetail';

import ReaderHomepage from './pages/ReaderHomepage';
import ShelfDetailPage from './pages/ShelfDetailPage';
import Community from './pages/Community';
import ReanloIntro from './components/shared/ReanloIntro';
import ReaderProfile from './pages/ReaderProfile';
import PublicProfile from './pages/PublicProfile';
import AdminPortal from './pages/AdminPortal';
import { ToastProvider } from './components/Toast';
import FeedbackWidget from './components/shared/FeedbackWidget';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import HowMatchingWorksPage from './pages/HowMatchingWorksPage';
import SettingsPage from './pages/SettingsPage';
import AuthCallback from './pages/AuthCallback';
import AuthorRedirectModal from './components/shared/AuthorRedirectModal';
import ReaderCorner from './pages/ReaderCorner';
import ComingSoon, { COMING_SOON_COLOURS } from './components/shared/ComingSoon';

import * as Sentry from '@sentry/react';

function ErrorButton() {
  return (
    <button
      onClick={() => {
        throw new Error('This is your first error!');
      }}
    >
      Break the world
    </button>
  );
}

// Renders the feedback widget everywhere EXCEPT auth pages and /admin
function GlobalFeedbackWidget() {
  const { pathname } = useLocation();
  const hideOn = ['/login', '/signup', '/forgot-password', '/reset-password', '/verify-email', '/admin'];
  if (hideOn.some(p => pathname.startsWith(p))) return null;
  return <FeedbackWidget portal="reader" />;
}

// Handles the / route — waits for Supabase to parse the OAuth hash
// before redirecting, so the #access_token never gets stripped
function RootRedirect() {
  const navigate = useNavigate();
  const { hash } = useLocation();

  useEffect(() => {
    // If there's an OAuth token in the hash, do nothing —
    // Supabase's detectSessionInUrl will parse it and fire onAuthStateChange
    if (hash && hash.includes('access_token')) return;
    navigate('/profile', { replace: true });
  }, [hash, navigate]);

  return null;
}

// Keeps <title>, description and the canonical address in step with the route.
// Must live inside BrowserRouter to see location changes. Renders nothing.
function RouteSeo() {
  const { pathname } = useLocation();
  useEffect(() => { applyRouteSeo(pathname); }, [pathname]);
  return null;
}

export default function App() {
  return (
    <BrowserRouter>
      <ToastProvider>
        <RouteSeo />
           <AuthorRedirectModal /> 
        <GlobalFeedbackWidget />

        <Routes>
          {/* ── Auth pages — no gate, no navbar ───────────────────────── */}
          <Route path="/login"           element={<LoginPage />} />
          <Route path="/signup"          element={<SignupPage />} />
          <Route path="/auth/callback"   element={<AuthCallback />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password"  element={<ResetPasswordPage />} />
          <Route path="/verify-email"    element={<VerifyEmailPage />} />
          <Route path='/terms'           element={<TermsOfService />} />
          <Route path='/privacy'         element={<PrivacyPolicy />} />
          <Route path='/how-matching-works' element={<HowMatchingWorksPage />} />

          {/* ── Public profile — outside AppLayout ────────────────────── */}
          <Route path="/u/:username" element={<PublicProfile />} />

          {/* ── App pages — navbar via AppLayout ──────────────────────── */}
          <Route element={<AppLayout />}>
            <Route path="/"            element={<RootRedirect />} />
            <Route
              path="/discover"
              element={
                <ComingSoon
                  accent={COMING_SOON_COLOURS.rust}
                  headline={<>reading beyond<br />the <em style={{ fontStyle: 'italic', color: COMING_SOON_COLOURS.rust }}>algorithm</em></>}
                />
              }
            />
            <Route path="/staging"     element={<ReaderHomepage />} /> 
            <Route path="/book/:bookId" element={<ProtectedRoute><BookDetail /></ProtectedRoute>} />
            <Route path="/community"   element={<Community />} />
            <Route path="/profile"     element={<ReaderProfile />} />
            <Route path="/settings"    element={<SettingsPage />} />
            <Route path="/admin"          element={<Navigate to="/admin/queue" replace />} />
            <Route path="/admin/:section" element={<ProtectedRoute><AdminPortal /></ProtectedRoute>} />
            <Route path="/reader-corner" element={<ReaderCorner />} />
            <Route path="*"            element={<Navigate to="/" replace />} />
          </Route>
        </Routes>
      </ToastProvider>
    </BrowserRouter>
  );
}